import * as theme from "../theme";

import { useContext, useEffect } from "react";

import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import Spinner from "../components/Spinner";
import Context from "../contexts/data";
import { getLenderByBuyer } from "../util/lenders";
import {
  AARP_OFFER_BANNER_ALT,
  AARP_OFFER_BODY,
  AARP_OFFER_CTA,
  AARP_OFFER_DISCLOSURE,
  AARP_OFFER_HEADLINE,
  buildAarpOfferUrl,
  showAarpOffer,
} from "../util/aarpOffer";
import AarpBannerSrc from "../assets/images/aarp_membership_700x400.jpg";

// Route props for the cold-load outcome URLs a partner-hosted form redirects
// to after posting to our API (the consumer never submitted in-app, so the
// context is empty and the route has to select the branch on first paint):
// - `forceSubmitProblem` (/result-problem): our response was not Matched;
//   render the same submit-problem message the in-app flow shows.
// - `forceMatchedLender` (/result-american-senior): our response named a
//   lender with an approved call-out; render exactly what the in-app flow
//   renders for that lender. It comes from the same LENDERS table, so the
//   route can never name a lender the in-app flow would not.
// Neither prop adds copy of its own or mutates state, so there is no flash
// of the forwarded page first. The submit-problem branch always wins.
const Result = ({ forceSubmitProblem = false, forceMatchedLender = null }) => {
  const { isFetchResult, matchedLender, matchedBuyer, submitProblem, firstName, lastName, handleUpdateFormValue } = useContext(Context);
  const [searchParams] = useSearchParams();
  const effectiveMatchedLender = forceMatchedLender || matchedLender;

  // The buyer this page is about. A forced-lender route names it; otherwise
  // it is what the in-app submit stored (null when the matched post named no
  // buyer). undefined means nothing is known: a cold load of /result after a
  // partner-hosted form, where the buyer could be FAR.
  const effectiveBuyer = forceMatchedLender ? forceMatchedLender.apiName : matchedBuyer;

  useEffect(() => {
    if (!window.location.href.includes("localhost")) return;
    const mockBuyer = searchParams.get("mock_buyer");
    if (mockBuyer) {
      const lender = getLenderByBuyer(mockBuyer);
      if (lender) handleUpdateFormValue("matchedLender", lender);
      // ?mock_buyer=FAR hides the AARP offer; ?mock_buyer=Guild shows it.
      handleUpdateFormValue("matchedBuyer", mockBuyer);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fullName =
    firstName && lastName ? `${firstName} ${lastName}` : "RateZip User";

  let lenderMatchContent = (
    <SmallerTextContainer>
      <SmallerText>
        Your information has been forwarded to a participating lender.
      </SmallerText>
    </SmallerTextContainer>
  );

  if (effectiveMatchedLender) {
    const { logoPosition = "below", align = "center" } = effectiveMatchedLender.layout || {};

    // The NMLS disclosure is rendered whenever the lender carries one, with
    // or without a logo: a name-only lender (no logo asset yet) must still
    // show its licensing line.
    const logoBlock = (effectiveMatchedLender.logoSrc || effectiveMatchedLender.nmls) && (
      <LogoContainer $align={align}>
        {effectiveMatchedLender.logoSrc && (
          effectiveMatchedLender.url ? (
            <a href={effectiveMatchedLender.url} target="_blank" rel="noreferrer">
              <LenderLogo src={effectiveMatchedLender.logoSrc} alt={effectiveMatchedLender.displayName} />
            </a>
          ) : (
            <LenderLogo src={effectiveMatchedLender.logoSrc} alt={effectiveMatchedLender.displayName} />
          )
        )}
        {effectiveMatchedLender.nmls && <p>{effectiveMatchedLender.nmls}</p>}
      </LogoContainer>
    );

    const textBlock = (
      <SmallerTextContainer>
        <SmallerText $align={align}>{effectiveMatchedLender.adCopy}</SmallerText>
      </SmallerTextContainer>
    );

    // The "Next Step: See Your Credit Score" line that followed the lender's
    // block (score portal link-out, Paul 2026-09-15) was removed 2026-10-17:
    // the score portal is down and Paul ordered every thank-you link-out to
    // it gone. For a known non-FAR buyer the only thing that follows the lender's
    // block is the AARP offer (aarpOfferBlock below); otherwise it is last.

    lenderMatchContent = (
      <>
        {logoPosition === "above" ? logoBlock : null}
        {textBlock}
        {logoPosition !== "above" ? logoBlock : null}
      </>
    );
  }

  // AARP membership offer (Impact affiliate via Strand; Paul, 2026-10-16).
  // Rendered below the lender block, never while the post is in flight, and
  // only when the buyer is KNOWN and is not FAR: a FAR-matched applicant must
  // see nothing beyond the neutral forwarded page, and a cold load cannot
  // tell whether FAR bought the lead, so it shows nothing either.
  // Copy and the banner alt text live in util/aarpOffer.js, written to
  // Strand's 51/49 Copy Guidance; the banner is AARP's own creative. Strand
  // (Chelsea Chapman) has not yet approved the copy or the placement: AARP's
  // Media Restrictions exclude reverse mortgage properties. See aarpOffer.js.
  const aarpOfferBlock =
    effectiveBuyer !== undefined && showAarpOffer(effectiveBuyer) ? (
      <AarpOfferContainer data-testid="aarp-offer">
        <AarpOfferBannerLink
          href={buildAarpOfferUrl(effectiveBuyer)}
          target="_blank"
          rel="noopener sponsored"
        >
          <AarpOfferBanner src={AarpBannerSrc} alt={AARP_OFFER_BANNER_ALT} />
        </AarpOfferBannerLink>
        <AarpOfferHeadline>{AARP_OFFER_HEADLINE}</AarpOfferHeadline>
        <AarpOfferText>{AARP_OFFER_BODY}</AarpOfferText>
        <AarpOfferButton
          href={buildAarpOfferUrl(effectiveBuyer)}
          target="_blank"
          rel="noopener sponsored"
        >
          {AARP_OFFER_CTA}
        </AarpOfferButton>
        <AarpOfferDisclosure>{AARP_OFFER_DISCLOSURE}</AarpOfferDisclosure>
      </AarpOfferContainer>
    ) : null;

  if (isFetchResult) {
    return (
      <Wrapper>
        <HeadBiggerTextContainer>
          <HeadBiggerText>Thank you {fullName}.</HeadBiggerText>
        </HeadBiggerTextContainer>
        <Spinner text="Loading your offers..." />
      </Wrapper>
    );
  }

  // Regulatory requirement (VA mortgage exam, 2026-08): the consumer must be
  // notified when their submission could not be matched or delivered.
  if (submitProblem || forceSubmitProblem) {
    return (
      <Wrapper>
        <HeadWrapper>
          <HeadBiggerTextContainer>
            <HeadBiggerText>
              We apologize. There was a problem submitting your data.
            </HeadBiggerText>
          </HeadBiggerTextContainer>
        </HeadWrapper>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <HeadWrapper>
        <HeadBiggerTextContainer>
          <HeadBiggerText>
            Thank you for submitting your request.
          </HeadBiggerText>
        </HeadBiggerTextContainer>
      </HeadWrapper>
      <TextsContainer>
        <BiggerTextContainer>
          <BiggerText></BiggerText>
        </BiggerTextContainer>
        {lenderMatchContent}
        {aarpOfferBlock}
      </TextsContainer>
      <BottomTextContainer>
        <BottomText>
          Your Request: RateZip receives compensation for connecting consumers with participating mortgage lenders. Not all lenders may be included. Any potential loan terms would be determined solely by the lender after completion of a mortgage application and verification of required information.
        </BottomText>
      </BottomTextContainer>
    </Wrapper>
  );
};

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${({ $align }) => $align === "left" ? "flex-start" : "center"};
  gap: 0.5rem;
  margin: 3rem 0;
  p {
    font-family: "Inter";
    font-style: normal;
    font-weight: 400;
    color: #666666;
    font-size: 1rem;
    margin: 0;

    @media (max-width: 900px) {
      font-size: 0.75rem;
    }
  }
`;

const LenderLogo = styled.img`
  width: 150px;
`;

const Wrapper = styled(theme.column_align)`
  width: 95%;
  max-width: 900px;
  min-height: 45vh;
  margin-top: 2rem;
`;

const HeadWrapper = styled.div`
  width: 100%;
`;

const HeadBiggerTextContainer = styled.div``;

const HeadBiggerText = styled.h1`
  font-family: "Inter";
  font-style: normal;
  font-weight: 600;
  font-size: 48px;
  line-height: 64px;
  margin-bottom: 16px;
  margin-top: 0px;
  @media (max-width: ${theme.mobile}) {
    font-size: 24px;
    line-height: 32px;
  }
`;

const TextsContainer = styled.div`
  width: 100%;
`;

const BiggerTextContainer = styled.div``;

const BiggerText = styled.h2`
  font-family: "Inter";
  font-style: normal;
  font-weight: 600;
  font-size: 32px;
  line-height: 40px;
  margin: 32px 0px 0px 0px;
  @media (max-width: ${theme.mobile}) {
    font-size: 18px;
    line-height: 32px;
  }
`;

const SmallerTextContainer = styled.div``;

const SmallerText = styled.p`
  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  font-size: 1.5rem;
  text-align: ${({ $align }) => $align || "center"};
  line-height: 24px;
  color: #000000;
  margin: 0px 0px 16px 0px;
  @media (max-width: ${theme.mobile}) {
    font-size: 14px;
  }
`;

const AarpOfferContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
  margin: 2rem 0 1rem 0;
  padding: 1.5rem 1rem;
  border-top: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.75rem;
`;

// AARP's own 700x400 banner, scaled to the block width and never upscaled.
const AarpOfferBannerLink = styled.a`
  display: block;
  width: 100%;
  max-width: 600px;
  line-height: 0;
`;

const AarpOfferBanner = styled.img`
  display: block;
  width: 100%;
  height: auto;
  border-radius: 6px;
`;

const AarpOfferHeadline = styled.h3`
  font-family: "Inter";
  font-style: normal;
  font-weight: 600;
  font-size: 1.25rem;
  line-height: 28px;
  color: #000000;
  margin: 0;
  @media (max-width: ${theme.mobile}) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const AarpOfferText = styled.p`
  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  line-height: 24px;
  color: #000000;
  margin: 0;
  max-width: 600px;
  @media (max-width: ${theme.mobile}) {
    font-size: 14px;
    line-height: 20px;
  }
`;

const AarpOfferButton = styled.a`
  display: inline-block;
  font-family: "Inter";
  font-weight: 600;
  font-size: 1rem;
  line-height: 24px;
  color: #ffffff;
  background: ${theme.primary_light};
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  text-decoration: none;
  max-width: 100%;
  @media (max-width: ${theme.mobile}) {
    font-size: 14px;
    width: 100%;
    box-sizing: border-box;
  }
`;

const AarpOfferDisclosure = styled.p`
  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #666666;
  margin: 0;
`;

const BottomTextContainer = styled.div``;

const BottomText = styled.p`
  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #666666;
`;

export default Result;
